import * as THREE from 'three';
import type { BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { lookPoint, looksRng, Precip, SkyLook, TAU, type OmenCtx, type OmenScene } from './common.ts';
import { GroundMarks, MARK } from './marks.ts';

/**
 * The storm: a dark, cold sky, slanting rain (dust sifting from the ceiling in the crypt), far lightning
 * that lights the whole board for an instant, and the bolts of the sim: a crackling ring on the ground
 * that closes as the strike comes, then a jagged bolt from the clouds, a white flash, a shock ring, sparks
 * and a scorch that smoulders a moment and stays a while.
 */

const CORE = new THREE.Color('#f2f7ff');
const HALO = new THREE.Color('#7fb2ff');
const DEEP = new THREE.Color('#3a5cff');

export const buildStorm = (ctx: OmenCtx): OmenScene => {
  const group = new THREE.Group();
  group.name = 'omen-storm';
  const low = ctx.low;
  const rnd = looksRng(0x570e);
  const sky = new SkyLook(ctx, {
    hemi: ['#8a98b8', '#1c2028', 0.6],
    key: ['#a8bcff', 0.45],
    rim: ['#7f9cff', 1.1],
    background: '#1a1e27',
    fog: '#1c2029',
    mist: { low: '#3a414f', mid: '#2a303b', far: '#161a21', alpha: 1.25 },
  });
  const marks = new GroundMarks(40, 16);
  const glow = new GlowLayer(256);
  group.add(marks.group, glow.mesh);
  const rain = ctx.crypt
    ? new Precip({ count: low ? 120 : 320, box: [11, 7, 11], fall: 1.1, sway: 0.15, size: [0.035, 0.035], color: '#8f8a80', opacity: 0.55, round: true }, 31)
    : new Precip({ count: low ? 520 : 1500, box: [15, 11, 15], fall: 16, wind: [0.9, 0.35], size: [0.018, 0.5], color: '#b9c9e6', opacity: 0.42 }, 31);
  group.add(rain.mesh);

  /** Longest warning seen per bolt, to tell how near it is. */
  const warnOf = new Map<number, number>();
  /** Bolts about to strike: world point, radius, fill. */
  let pending: { p: THREE.Vector3; r: number; fill: number; seed: number }[] = [];
  /** Recent strikes for the glow that lingers on the spot (seconds). */
  const strikes: { p: THREE.Vector3; at: number }[] = [];
  let now = 0;
  let nextFar = 4 + rnd() * 6;
  let flashAt = -10;
  let flashBig = 0;
  const flashCol = new THREE.Color('#9fb4e8');
  const look = new THREE.Vector3();

  const tick = (s: BattleState): void => {
    pending = [];
    const live = new Set<number>();
    for (const z of s.zones) {
      if (z.kind !== 'bolt' || (z.warn ?? 0) <= 0) continue;
      live.add(z.id);
      const max = Math.max(warnOf.get(z.id) ?? 0, z.warn as number);
      warnOf.set(z.id, max);
      const r = (z.radius / 1000) * 1.2;
      pending.push({ p: ctx.at(z.x, z.y, 0.02), r, fill: 1 - (z.warn as number) / max, seed: (z.id % 97) / 97 });
    }
    for (const id of [...warnOf.keys()]) if (!live.has(id)) warnOf.delete(id);
    marks.begin();
    for (const b of pending) {
      marks.add(MARK.bolt, b.p, b.r, b.fill, b.seed);
      // Static in the air over the spot, more as it comes.
      if (rnd() < 0.25 + b.fill * 0.6) {
        const a = rnd() * TAU;
        const d = rnd() * b.r * 0.9;
        const q = b.p.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.05, Math.sin(a) * d));
        ctx.vfx.beam(q, q.clone().add(new THREE.Vector3((rnd() - 0.5) * 0.5, 0.25 + rnd() * 0.5, (rnd() - 0.5) * 0.5)), '#bcd6ff', 1, 0.012, 0.6);
      }
    }
    marks.end();
    // Splashes where the rain lands near the camera's look.
    if (!ctx.crypt) {
      const n = low ? 1 : 3;
      for (let i = 0; i < n; i++) {
        const x = look.x + (rnd() - 0.5) * 16, z = look.z + (rnd() - 0.5) * 12;
        ctx.vfx.ring(new THREE.Vector3(x, ctx.ground(x, z) + 0.02, z), 0.02, 0.16, '#9fb4d8', 4, 0.35);
      }
    }
  };

  /** A bolt from the clouds: a jagged spine and a few forks, each a stack of beams (core, halo). */
  const bolt = (p: THREE.Vector3, r: number): void => {
    const top = p.clone().add(new THREE.Vector3((rnd() - 0.5) * 3, ctx.crypt ? 6 : 16, (rnd() - 0.5) * 3));
    const pts: THREE.Vector3[] = [top];
    const segs = 9;
    for (let i = 1; i < segs; i++) {
      const k = i / segs;
      const q = top.clone().lerp(p, k);
      const j = (1 - k) * 1.6 + 0.25;
      q.x += (rnd() - 0.5) * j;
      q.z += (rnd() - 0.5) * j;
      pts.push(q);
    }
    pts.push(p.clone().setY(p.y + 0.05));
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i] as THREE.Vector3, b = pts[i + 1] as THREE.Vector3;
      ctx.vfx.beam(a, b, HALO, 4, 0.11, 0);
      ctx.vfx.beam(a, b, CORE, 3, 0.05, 0);
      // Forks off the spine, the lower ones longer.
      if (i > 1 && rnd() < 0.45) {
        const end = b.clone().add(new THREE.Vector3((rnd() - 0.5) * 2.4, -0.4 - rnd() * 1.4, (rnd() - 0.5) * 2.4));
        if (end.y < p.y) end.y = p.y + 0.1;
        ctx.vfx.beam(b, end, HALO, 3, 0.06, 0.5);
      }
    }
    // Arcs crawling over the ground under it.
    for (let i = 0; i < (low ? 3 : 6); i++) {
      const a = rnd() * TAU;
      const end = p.clone().add(new THREE.Vector3(Math.cos(a) * r * (0.6 + rnd() * 0.4), 0.04, Math.sin(a) * r * (0.6 + rnd() * 0.4)));
      ctx.vfx.beam(p.clone().setY(p.y + 0.05), end, '#cfe0ff', 3, 0.03, 0.7);
    }
  };

  const strike = (p: THREE.Vector3, r: number): void => {
    bolt(p, r);
    const up = (h: number): THREE.Vector3 => p.clone().setY(p.y + h);
    ctx.flare(up(0.4), '#ffffff', r * 1.4, 3);
    // Nothing past the bolt's own line (r): the wave went to r·1.25 and the glow to r·1.3 and read as a hit outside.
    ctx.flare(up(0.3), '#8fb6ff', r * 2, 9);
    ctx.vfx.ring(p, r * 0.1, r, '#d8e6ff', 6, 1);
    ctx.vfx.ring(up(0.05), r * 0.05, r * 0.8, '#6f9cff', 9, 0.8);
    ctx.vfx.burst(up(0.15), { n: low ? 10 : 24, color: '#dfeaff', speed: 0.16, up: 0.12, size: 0.07, life: 8, gravity: 0.014 });
    ctx.vfx.burst(up(0.1), { n: low ? 6 : 12, color: '#7fb2ff', speed: 0.1, up: 0.16, size: 0.1, life: 10, gravity: 0.01 });
    ctx.vfx.burst(up(0.2), { n: low ? 4 : 9, color: '#3c4150', speed: 0.05, up: 0.05, size: 0.55, life: 18, smoke: true, spread: r * 0.5 });
    marks.put(MARK.scorch, p, r * 0.75, 40, rnd());
    strikes.push({ p: up(0.25), at: now });
    if (strikes.length > 12) strikes.shift();
    ctx.shake(0.3);
    flashAt = now;
    flashBig = 1;
  };

  return {
    group,
    tick,
    event: (_s, e) => {
      if (e.t !== 'boom' || e.kind !== 'bolt') return false;
      strike(ctx.at(e.x, e.y, 0.02), (e.r / 1000) * 1.2);
      return true;
    },
    frame: (t, camera) => {
      now = t;
      marks.frame(t);
      lookPoint(camera, look);
      rain.frame(t, look);
      // Far lightning now and then: a double flicker of cold light over everything.
      if (t > nextFar) {
        flashAt = t;
        flashBig = ctx.crypt ? 0.35 : 0.7;
        nextFar = t + 6 + rnd() * 10;
      }
      const dt = t - flashAt;
      const f = (dt < 0 ? 0 : dt < 0.06 ? 1 : dt < 0.12 ? 0.2 : dt < 0.2 ? 0.8 : Math.max(0, 1 - (dt - 0.2) / 0.35) * 0.35) * flashBig;
      sky.pulse({ hemi: 1 + 2.2 * f, key: 1 + 2.5 * f, rim: 1 + 1.5 * f }, flashCol, f * 0.7);
      // Where a bolt fell glows blue a moment, and spots soon to be struck hum.
      glow.begin();
      for (const st of strikes) {
        const k = 1 - (t - st.at) / 1.6;
        if (k > 0) glow.add(st.p.x, st.p.y, st.p.z, DEEP, 2.4 * k, 0.5 * k);
      }
      for (const b of pending) glow.add(b.p.x, b.p.y + 0.3, b.p.z, HALO, b.r * (0.5 + b.fill), 0.12 + 0.35 * b.fill * (0.6 + 0.4 * Math.sin(t * 40)));
      glow.end();
    },
    dispose: () => {
      sky.restore();
      marks.dispose();
      rain.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
